"use client";

import React from "react";
import PropTypes from "prop-types";
import NextLink from "next/link";
import {
  isLocalURL,
  isSubdomain,
  concatenateQueryParams,
} from "@gonoodle/gn-universe-utils";

import { ROUTE_PREFIX } from "../../constants";
import { useLogEvents } from "../../contexts/Analytics";
import { useReferrer } from "../../contexts/Referrer";
import { useRouter } from "../../hooks";

function resolveExternalHref(href) {
  const path = typeof href === "string" ? href : href.pathname;
  const query = typeof href === "string" ? {} : href.query;

  return concatenateQueryParams(`/${ROUTE_PREFIX.EXTERNAL_LINK_DISCLAIMER}`, {
    href: path,
    ...query,
  });
}

const Link = React.forwardRef(function Link(
  {
    href,
    children,
    events = [],
    referrer = {},
    as = href,
    onPress = () => {},
    ...rest
  },
  forwardedRef,
) {
  const router = useRouter();
  const { logEvents } = useLogEvents({
    events,
    options: {
      includeReferrer: false,
      includeSourcePage: true,
      includeSourcePageType: true,
    },
  });
  const { setReferrer } = useReferrer();
  const path = typeof href === "string" ? href : href.pathname;

  const handleLinkPress = (e) => {
    onPress(e);
    if (e.defaultPrevented) return;

    setReferrer(referrer);

    logEvents();

    if (
      !isLocalURL(path) &&
      !isSubdomain(process.env.NEXT_PUBLIC_WEB_URL, path)
    ) {
      e.preventDefault();
      router.push(resolveExternalHref(href));
    }
  };

  return (
    <NextLink
      href={href}
      as={as}
      ref={forwardedRef}
      onClick={handleLinkPress}
      {...rest}
    >
      {children}
    </NextLink>
  );
});

Link.propTypes = {
  href: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      pathname: PropTypes.string.isRequired,
      query: PropTypes.object,
    }),
  ]).isRequired,
  as: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      pathname: PropTypes.string.isRequired,
      query: PropTypes.object,
    }),
  ]),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  events: PropTypes.arrayOf(
    PropTypes.shape({
      event: PropTypes.string.isRequired,
      properties: PropTypes.shape({}).isRequired,
    }),
  ),
  referrer: PropTypes.shape({
    sourcePage: PropTypes.string,
    sourcePageType: PropTypes.string,
    sourceElement: PropTypes.string,
    sourceName: PropTypes.string,
  }),
  onPress: PropTypes.func,
};

export default Link;
